import { useEffect, useId, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import type { AccountSuggestion } from "../../../api/access/accessModel";
import ErrorNotice from "../../../errors/ErrorNotice";
import { loadAfterTypingPause, type PausedLoadState } from "../../../shared/loading/afterTypingPause";
import Field from "../../../ui/forms/Field";
import SuggestionList from "../../../ui/forms/SuggestionList";
import { nextSuggestionIndex, suggestionOptionId } from "../../../ui/forms/suggestionNavigation";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";

/** Accounts that match what was typed; the server decides how much typing is enough. */
export type SuggestPeople = (query: string, signal: AbortSignal) => Promise<readonly AccountSuggestion[]>;

interface PersonSuggestFieldProps {
  label: string;
  suggest: SuggestPeople;
  /** Called with the account chosen from the suggestions. */
  onChoose: (suggestion: AccountSuggestion) => void;
  /** Called as the typing changes: what is typed, and whether the lookup found nobody for it. */
  onTyped?: (typed: string, nothingMatches: boolean) => void;
  /** Cleared after each choice, so the next person can be typed. */
  clearOnChoose?: boolean;
  describedBy?: string;
  autoFocus?: boolean;
}

interface FoundAccounts {
  readonly query: string;
  readonly state: PausedLoadState<readonly AccountSuggestion[]>;
}

/**
 * One field that finds an account (F15): typing a name, or a whole email, suggests accounts
 * of the installation with a masked email to tell namesakes apart; the arrow keys and Enter
 * choose one, as a combobox does (WAI-ARIA APG).
 */
export default function PersonSuggestField({ label, suggest, onChoose, onTyped, clearOnChoose = false, describedBy, autoFocus = false }: PersonSuggestFieldProps) {
  const { t } = useTranslation();
  const fieldId = useId();
  const listId = `${fieldId}-suggestions`;
  const [text, setText] = useState("");
  const [chosen, setChosen] = useState<AccountSuggestion | null>(null);
  const [found, setFound] = useState<FoundAccounts | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const query = text.trim();
  useEffect(() => {
    if (chosen !== null || query === "") return undefined;
    return loadAfterTypingPause((signal) => suggest(query, signal), (loaded) => { setFound({ query, state: loaded }); });
  }, [suggest, query, chosen]);
  // Only the answer to what is typed now; an older one may still be on its way.
  const current = chosen === null && found?.query === query ? found.state : null;
  const suggestions = listOpen && current?.status === "loaded" ? current.value : [];
  const nothingMatches = current?.status === "loaded" && current.value.length === 0;
  const active = Math.min(activeIndex, suggestions.length - 1);

  useEffect(() => {
    onTyped?.(query, nothingMatches);
  }, [onTyped, query, nothingMatches]);

  const choose = (suggestion: AccountSuggestion) => {
    setListOpen(false);
    if (clearOnChoose) {
      setText("");
      setChosen(null);
    } else {
      setChosen(suggestion);
      setText(suggestion.displayName);
    }
    onChoose(suggestion);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const next = nextSuggestionIndex(event.key, active, suggestions.length);
    if (next !== null) {
      event.preventDefault();
      setActiveIndex(next);
    } else if (event.key === "Enter" && suggestions[active] !== undefined) {
      event.preventDefault();
      choose(suggestions[active]);
    } else if (event.key === "Escape" && suggestions.length > 0) {
      event.preventDefault();
      setListOpen(false);
    }
  };

  return (
    <div className="relative min-w-60 flex-1 space-y-1">
      <Field id={`${fieldId}-person`} label={label}>
        {(control) => (
          <input
            {...control}
            type="text"
            role="combobox"
            autoComplete="off"
            // Only for a field the user's own click just opened, to type into at once.
            autoFocus={autoFocus}
            aria-autocomplete="list"
            aria-expanded={suggestions.length > 0}
            aria-controls={listId}
            aria-activedescendant={suggestions.length > 0 ? suggestionOptionId(listId, active) : undefined}
            aria-describedby={describedBy}
            className={FORM_CONTROL_CLASS}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setChosen(null);
              setListOpen(true);
              setActiveIndex(0);
            }}
            onKeyDown={onKeyDown}
            onFocus={() => { setListOpen(true); }}
            onBlur={() => { setListOpen(false); }}
          />
        )}
      </Field>
      {suggestions.length > 0 ? (
        <SuggestionList
          id={listId}
          label={t("projects.members.add.suggestions")}
          items={suggestions}
          activeIndex={active}
          itemKey={(suggestion) => suggestion.userId}
          renderItem={(suggestion) => (
            <>
              <span className="font-medium">{suggestion.displayName}</span>
              <span className="app-text-muted ml-2 text-xs">{suggestion.emailHint}</span>
            </>
          )}
          onChoose={choose}
          placement="below"
        />
      ) : null}
      {current?.status === "failed" ? <ErrorNotice error={current.error} /> : null}
    </div>
  );
}
